import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import TargetSettingsTab from "./TargetSettingsTab";
import DefinitionTab from "./DefinitionTab";
import SegmentMapTab from "./SegmentMapTab";
import ProdCap from "./ProdCap";
import * as Fa from "react-icons/fa";
import { fetchData, postData } from "../../../Services/GlobalService";
import { format } from "date-fns";
import Loader from "../../../Components/Loader";
import { toast } from "react-toastify";

export type ConstructType = "B2C" | "HDSSD" | "INDUSTRIAL";

export interface PerformanceTarget {
  threshold: number;
  target: number;
  stretch: number;
  mega?: number;
}

export interface EarningTarget {
  threshold: number;
  target: number;
  stretch: number;
}

export type EarningType = "Credit Days" | "Earning %";

export interface EarningCreditDays {
  threshold: number;
  target: number;
  stretch: number;
}

export interface KpiRow {
  id: string;
  paramId: number;
  parameter: string;
  type: string;
  applicableOn: string;
  odt?: string;
  basis: string;
  weightage?: number;
  performance: PerformanceTarget;
  earning?: EarningTarget;
  earningType?: EarningType;
  earningCreditDays?: EarningCreditDays;
}

export interface KpiSegmentMapping {
  kpiId: string;
  paramId: number;
  parameter: string;
  applicableOn: string;
  streams: string[];
  channels: string[];
}

export interface SegmentMapSection {
  mappings: KpiSegmentMapping[];
}

export interface KpiTargetUpload {
  kpiId: string;
  paramId: number;
  parameter: string;
  fileName: string;
  fileData: Record<string, unknown>[] | null;
  uploaded: boolean;
  uploadError: string;
}

export interface TargetSettingsSection {
  uploads: KpiTargetUpload[];
}

export interface ConstructFormData {
  constructName: string;
  orderMode: { ORD_MODE_ID: string; ORD_MODE_DESC: string }[];
  startDate: Date | null;
  endDate: Date | null;

  b2c: { kpis: KpiRow[]; payoutTable: EarningTarget };
  hsssd: {
    kpis: KpiRow[];
    payoutLabel: string;
    payoutTable: PerformanceTarget;
  };
  industrial: {
    kpis: KpiRow[];
    payoutLabel: string;
    payoutTable: PerformanceTarget;
  };

  segmentMap: {
    b2c: SegmentMapSection;
    hsssd: SegmentMapSection;
    industrial: SegmentMapSection;
  };

  targetSettings: {
    b2c: TargetSettingsSection;
    hsssd: TargetSettingsSection;
    industrial: TargetSettingsSection;
  };
}

type SectionKey = "b2c" | "hsssd" | "industrial";

const toKey = (ct: ConstructType): SectionKey =>
  ct === "B2C" ? "b2c" : ct === "HDSSD" ? "hsssd" : "industrial";

const toKeyapi = (ct: ConstructType) =>
  ct === "B2C" ? "" : ct === "HDSSD" ? "HDSSD" : "IND";

const normaliseConstructType = (raw: string | null): ConstructType => {
  if (!raw) return "B2C";
  const upper = raw.toUpperCase();
  if (upper === "HDSSD") return "HDSSD";
  if (upper === "INDUSTRIAL DB" || upper === "INDUSTRIAL") return "INDUSTRIAL";
  return "B2C";
};

interface ApiParam {
  PARAM_ID: number;
  PARAM_NAME: string;
  Applicable_On: string;
}

const Controller = "ConstructDBSIP";
const DbSipEndPoint = {
  GetParameterData: "GetParameterData",
  SaveDefination: "SaveDefination",
  GetConstructDefById: "GetConstructDefById",
  SaveDefination_HDSSDIND: "SaveDefination_HDSSDIND",
};

const buildB2CKpisFromApi = (params: ApiParam[]): KpiRow[] =>
  params.map((p) => ({
    id: `b2c-kpi-${p.PARAM_ID}`,
    paramId: p.PARAM_ID,
    parameter: p.PARAM_NAME,
    type: p.PARAM_ID === 4 ? "Count" : "Volume",
    applicableOn: p.Applicable_On,
    basis: "Quarterly",
    performance: { threshold: 0, target: 0, stretch: 0 },
    earning: { threshold: 0, target: 0, stretch: 0 },
    earningType: "Earning %" as EarningType,
    earningCreditDays: { threshold: 0, target: 0, stretch: 0 },
  }));

const buildHSSSDKpisFromApi = (params: ApiParam[]): KpiRow[] =>
  params.map((p) => ({
    id: `hsssd-kpi-${p.PARAM_ID}`,
    paramId: p.PARAM_ID,
    parameter: p.PARAM_NAME,
    type: "Volume",
    applicableOn: p.Applicable_On,
    odt: "NO",
    basis: "Quarterly",
    weightage: 0,
    performance: { threshold: 0, target: 0, stretch: 0, mega: 0 },
  }));

const buildIndustrialKpisFromApi = (params: ApiParam[]): KpiRow[] =>
  params.map((p) => ({
    id: `ind-kpi-${p.PARAM_ID}`,
    paramId: p.PARAM_ID,
    parameter: p.PARAM_NAME,
    type: "Volume",
    applicableOn: p.Applicable_On,
    odt: "NO",
    basis: "Quarterly",
    weightage: 0,
    performance: { threshold: 0, target: 0, stretch: 0, mega: 0 },
  }));

//  KPI BUILDERS (edit construct — hydrate from API response)
const hydrateB2CKpisFromDef = (params: any[]): KpiRow[] =>
  params.map((p) => {
    const earningType: EarningType =
      p.EARNING_TYPE === "Credit Days" ? "Credit Days" : "Earning %";

    return {
      id: `b2c-kpi-${p.PARAM_ID}`,
      paramId: p.PARAM_ID,
      parameter: p.PARAM_NAME,
      type: p.PARAM_TYPE,
      applicableOn: p.APLLICABLE_ON,
      basis: p.BASIS,

      performance: {
        threshold: Number(p.PERF_THRESOLD ?? 0),
        target: Number(p.PERF_TARGET ?? 0),
        stretch: Number(p.PERF_STRETCH ?? 0),
      },

      // POINTS_ fields → Earning % values (decimal, 6dp)
      earning: {
        threshold: Number(p.POINTS_THRESOLD ?? 0),
        target: Number(p.POINTS_TARGET ?? 0),
        stretch: Number(p.POINTS_STRETCH ?? 0),
      },

      earningType,

      // TOTAL_ fields → Credit Days values (integer)
      earningCreditDays: {
        threshold: Number(p.TOTAL_THRESHOLD ?? 0),
        target: Number(p.TOTAL_TARGET ?? 0),
        stretch: Number(p.TOTAL_STRETCH ?? 0),
      },
    };
  });

const hydrateHSSSDKpisFromDef = (params: any[]): KpiRow[] =>
  params.map((p) => ({
    id: `hsssd-kpi-${p.PARAM_ID}`,
    paramId: p.PARAM_ID,
    parameter: p.PARAM_NAME,
    type: p.PARAM_TYPE ?? "Volume",
    applicableOn: p.APLLICABLE_ON ?? "",
    odt: p.ODT ?? "NO",
    basis: p.BASIS ?? "Quarterly",
    weightage: Number(p.PERF_WEIGHTAGE ?? 0),
    performance: {
      threshold: Number(p.PERF_THRESOLD ?? 0),
      target: Number(p.PERF_TARGET ?? 0),
      stretch: Number(p.PERF_STRETCH ?? 0),
      mega: Number(p.PERF_MEGA ?? 0),
    },
  }));

const hydrateIndustrialKpisFromDef = (params: any[]): KpiRow[] =>
  params.map((p) => ({
    id: `ind-kpi-${p.PARAM_ID}`,
    paramId: p.PARAM_ID,
    parameter: p.PARAM_NAME,
    type: p.PARAM_TYPE ?? "Volume",
    applicableOn: p.APLLICABLE_ON ?? "",
    odt: p.ODT ?? "NO",
    basis: p.BASIS ?? "Quarterly",
    weightage: Number(p.PERF_WEIGHTAGE ?? 0),
    performance: {
      threshold: Number(p.PERF_THRESOLD ?? 0),
      target: Number(p.PERF_TARGET ?? 0),
      stretch: Number(p.PERF_STRETCH ?? 0),
      mega: Number(p.PERF_MEGA ?? 0),
    },
  }));

const fetchParams = async (
  constructType: ConstructType,
): Promise<ApiParam[]> => {
  try {
    const key = toKeyapi(constructType);
    const schmres = await fetchData(
      Controller,
      DbSipEndPoint.GetParameterData,
      `STREAM=${key}`,
    );
    return (schmres?.Data ?? []) as ApiParam[];
  } catch (error) {
    console.error("API error:", error);
    return [];
  }
};

const fetchConstructDef = async (
  constructId: number,
  constructType: ConstructType,
) => {
  try {
    const res = await fetchData(
      Controller,
      DbSipEndPoint.GetConstructDefById,
      `CONSTRUCT_ID=${constructId}&STREAM=${constructType == "B2C" ? "" : constructType == "HDSSD" ? "HDSSD" : "IND"}&EARNING_TYPE=null`,
    );
    return res?.Data ?? null;
  } catch (error) {
    console.error("GetConstructDefById error:", error);
    return null;
  }
};

//  SEGMENT / TARGET / PRODCAP BUILDERS

const buildMappings = (kpis: KpiRow[]): KpiSegmentMapping[] =>
  kpis.map((k) => ({
    kpiId: k.id,
    paramId: k.paramId,
    parameter: k.parameter,
    applicableOn: k.applicableOn,
    streams: [],
    channels: [],
  }));

const buildTargetUploads = (kpis: KpiRow[]): KpiTargetUpload[] =>
  kpis.map((k) => ({
    kpiId: k.id,
    paramId: k.paramId,
    parameter: k.parameter,
    fileName: "",
    fileData: [],
    uploaded: false,
    uploadError: "",
  }));

//  EMPTY INITIAL FORM
const getEmptyForm = (): ConstructFormData => ({
  constructName: "",
  orderMode: [],
  startDate: null,
  endDate: null,
  b2c: { kpis: [], payoutTable: { threshold: 0, target: 0, stretch: 0 } },
  hsssd: {
    kpis: [],
    payoutLabel: "Payout as % of Primary Turnover (Net of all rebates)",
    payoutTable: { threshold: 0, target: 0, stretch: 0, mega: 0 },
  },
  industrial: {
    kpis: [],
    payoutLabel: "SIP Payout as % of Turnover (Primary)",
    payoutTable: { threshold: 0, target: 0, stretch: 0, mega: 0 },
  },
  segmentMap: {
    b2c: { mappings: [] },
    hsssd: { mappings: [] },
    industrial: { mappings: [] },
  },
  targetSettings: {
    b2c: { uploads: [] },
    hsssd: { uploads: [] },
    industrial: { uploads: [] },
  },
});

const UserId = localStorage.getItem("UserId")?.toString();

const extractCurrentTabData = (
  tabIndex: number,
  constructType: ConstructType,
  formData: ConstructFormData,
  constructSeq: number | null,
) => {
  const key = toKey(constructType);
  const meta = {
    tab: (["definition", "segmentMap", "targetSettings", "prodCap"] as const)[
      tabIndex
    ],
    constructType,
    constructSeq,
    constructName: formData.constructName,
    orderMode: formData.orderMode,
    startDate: formData.startDate,
    endDate: formData.endDate,
  };
  switch (tabIndex) {
    case 0:
      return {
        ...meta,
        data:
          key === "b2c"
            ? { kpis: formData.b2c.kpis, payoutTable: formData.b2c.payoutTable }
            : {
                kpis: formData[key].kpis,
                payoutLabel: (formData[key] as any).payoutLabel,
                payoutTable: formData[key].payoutTable,
              },
      };
    case 1:
      return { ...meta, data: formData.segmentMap[key] };
    case 2:
      return { ...meta, data: formData.targetSettings[key] };
    case 3:
      return { ...meta, data: null };
    default:
      return { ...meta, data: null };
  }
};

export default function Construct() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeIndex, setActiveIndex] = useState(0);
  const [constructType, setConstructType] = useState<ConstructType>(() =>
    normaliseConstructType(searchParams.get("constructType")),
  );
  const [formData, setFormData] = useState<ConstructFormData>(getEmptyForm());
  const [loadingKpis, setLoadingKpis] = useState(false);

  const [constructSeq, setConstructSeq] = useState<number | null>(() => {
    const fromId = searchParams.get("constructid");
    const fromSeq = searchParams.get("Construct_Seq");
    return fromId ? Number(fromId) : fromSeq ? Number(fromSeq) : null;
  });

  const [isCopyMode, setIsCopyMode] = useState(false);

  const isEditMode = Boolean(searchParams.get("constructid"));
  const isB2C = constructType === "B2C";
  const TOTAL_TABS = isB2C ? 4 : 3;

  const parseDate = (str: string) => {
    if (!str) return null;
    const [day, month, year] = str.split("/");
    return new Date(`${year}-${month}-${day}`);
  };
  useEffect(() => {
    let cancelled = false;
    setLoadingKpis(true);

    if (isEditMode && constructSeq) {
      fetchConstructDef(constructSeq, constructType).then((defData) => {
        if (cancelled || !defData) {
          setLoadingKpis(false);
          return;
        }
        const header = defData?.Table?.[0] || {};
        const paramList = defData?.Table1 || [];
        const payout = defData?.Table2?.[0] || {};
        const ordermode = (defData?.Table3 ?? []).map((om: any) => ({
          ORD_MODE_ID: om.Ord_Mode_ID ?? om.ORD_MODE_ID,
          ORD_MODE_DESC: om.ORD_MODE_DESC,
        }));

        let kpis: KpiRow[];
        if (constructType === "B2C") {
          kpis = hydrateB2CKpisFromDef(paramList);
        } else if (constructType === "HDSSD") {
          kpis = hydrateHSSSDKpisFromDef(paramList);
        } else {
          kpis = hydrateIndustrialKpisFromDef(paramList);
        }

        const key = toKey(constructType);

        setFormData((prev) => ({
          ...prev,
          constructName: header.Name ?? prev.constructName,
          orderMode: ordermode,
          startDate: parseDate(header.StartDate),
          endDate: parseDate(header.EndDate),
          [key]:
            key === "b2c"
              ? {
                  kpis,
                  payoutTable: {
                    threshold: Number(payout.Threshold_Percentage ?? 0),
                    target: Number(payout.Target_Percentage ?? 0),
                    stretch: Number(payout.Stretch_Percentage ?? 0),
                  },
                }
              : {
                  kpis,
                  payoutLabel:
                    key === "hsssd"
                      ? "Payout as % of Primary Turnover (Net of all rebates)"
                      : "SIP Payout as % of Turnover (Primary)",
                  payoutTable: {
                    threshold: Number(payout.Threshold_Percentage ?? 0),
                    target: Number(payout.Target_Percentage ?? 0),
                    stretch: Number(payout.Stretch_Percentage ?? 0),
                    mega: Number(payout.MegaStretch_Percentage ?? 0),
                  },
                },
          segmentMap: {
            ...prev.segmentMap,
            [key]: { mappings: buildMappings(kpis) },
          },
          targetSettings: {
            ...prev.targetSettings,
            [key]: { uploads: buildTargetUploads(kpis) },
          },
        }));

        setLoadingKpis(false);
      });
    } else {
      fetchParams(constructType).then((params) => {
        if (cancelled) return;

        const key = toKey(constructType);
        let kpis: KpiRow[];
        if (constructType === "B2C") {
          kpis = buildB2CKpisFromApi(params);
        } else if (constructType === "HDSSD") {
          kpis = buildHSSSDKpisFromApi(params);
        } else {
          kpis = buildIndustrialKpisFromApi(params);
        }

        setFormData((prev) => ({
          ...prev,
          [key]:
            key === "b2c"
              ? { kpis, payoutTable: { threshold: 0, target: 0, stretch: 0 } }
              : key === "hsssd"
                ? {
                    kpis,
                    payoutLabel:
                      "Payout as % of Primary Turnover (Net of all rebates)",
                    payoutTable: {
                      threshold: 0,
                      target: 0,
                      stretch: 0,
                      mega: 0,
                    },
                  }
                : {
                    kpis,
                    payoutLabel: "SIP Payout as % of Turnover (Primary)",
                    payoutTable: {
                      threshold: 0,
                      target: 0,
                      stretch: 0,
                      mega: 0,
                    },
                  },
          segmentMap: {
            ...prev.segmentMap,
            [key]: { mappings: buildMappings(kpis) },
          },
          targetSettings: {
            ...prev.targetSettings,
            [key]: { uploads: buildTargetUploads(kpis) },
          },
        }));

        setLoadingKpis(false);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [constructType, constructSeq, isEditMode]);

  const setFormDataWithSync: React.Dispatch<
    React.SetStateAction<ConstructFormData>
  > = (action) => {
    setFormData((prev) => {
      const next =
        typeof action === "function"
          ? (action as (s: ConstructFormData) => ConstructFormData)(prev)
          : action;

      const sections: SectionKey[] = ["b2c", "hsssd", "industrial"];
      let synced = { ...next };

      for (const key of sections) {
        const prevKpis = prev[key].kpis;
        const nextKpis = next[key].kpis;
        for (let i = 0; i < nextKpis.length; i++) {
          if (prevKpis[i]?.applicableOn !== nextKpis[i]?.applicableOn) {
            synced = {
              ...synced,
              segmentMap: {
                ...synced.segmentMap,
                [key]: {
                  mappings: synced.segmentMap[key].mappings.map((m) =>
                    m.kpiId === nextKpis[i].id
                      ? { ...m, applicableOn: nextKpis[i].applicableOn }
                      : m,
                  ),
                },
              },
            };
          }
        }
      }

      return synced;
    });
  };

  const buildPayload = (tabData: any) => ({
    pvc_UserID: UserId,
    pvc_ConstructName: tabData.constructName,
    order_mode: tabData?.orderMode
      ?.map((item: any) => item.ORD_MODE_ID)
      .join(","),
    pvc_StartDate: format(tabData.startDate ?? new Date(), "dd/MM/yyyy"),
    pvc_EndDate: format(tabData.endDate ?? new Date(), "dd/MM/yyyy"),
    pvc_Seq: isCopyMode ? "" : (constructSeq?.toString() ?? ""),

    pvc_ThresholdPercentage: "0",
    pvc_TargetPercentage: "0",
    pvc_StretchPercentage: "0",
    pvc_MegaStretchPercentage: "0",

    lstConsParam:
      tabData?.data?.kpis?.map((kpi: any) => {
        const isCreditDays = kpi.earningType === "Credit Days";
        return {
          pvc_IUKey: isCopyMode ? "I" : constructSeq ? "U" : "I",
          pvc_ConstructSeq: constructSeq?.toString() ?? "",
          pvc_ParameterSeq: kpi.paramId?.toString(),
          pvc_Type: kpi.type,
          pvc_ApplicableOn: kpi.applicableOn,
          pvc_Basis: kpi.basis,
          pvc_ThresholdPercentage:
            kpi.performance?.threshold?.toString() || "0",
          pvc_TargetPercentage: kpi.performance?.target?.toString() || "0",
          pvc_StretchPercentage: kpi.performance?.stretch?.toString() || "0",
          pvc_MegaStretchPercentage: "0",
          pvc_ThresholdPoint: kpi.earning?.threshold?.toString() || "0",
          pvc_TargetPoint: kpi.earning?.target?.toString() || "0",
          pvc_StretchPoint: kpi.earning?.stretch?.toString() || "0",
          pvc_MegaStretchPoint: "0",
          pvc_TotalThresholdPoint: (
            kpi.earningCreditDays?.threshold ?? 0
          ).toString(),
          pvc_TotalTargetPoint: (kpi.earningCreditDays?.target ?? 0).toString(),
          pvc_TotalStretchPoint: (
            kpi.earningCreditDays?.stretch ?? 0
          ).toString(),
          pvc_TotalMegaStretchPoint: "0",
          pvc_Status: "N",
          pvc_Earning_Type: isCreditDays ? "C" : "E",
        };
      }) || [],
  });

  const buildHDSSDPayload = (tabData: any) => ({
    pvc_UserID: UserId,
    pvc_ConstructName: tabData.constructName,
    pvc_StartDate: format(tabData.startDate ?? new Date(), "dd/MM/yyyy"),
    pvc_EndDate: format(tabData.endDate ?? new Date(), "dd/MM/yyyy"),
    pvc_Seq: isCopyMode ? "" : (constructSeq?.toString() ?? ""),

    pvc_ThresholdPercentage: tabData.data.payoutTable.threshold.toString(),
    pvc_TargetPercentage: tabData.data.payoutTable.target.toString(),
    pvc_StretchPercentage: tabData.data.payoutTable.stretch.toString(),
    pvc_MegaStretchPercentage: tabData.data.payoutTable.mega?.toString() ?? "0",
    pvc_Stream: tabData.constructType=="INDUSTRIAL"?"IND":tabData.constructType,

    lstConsParam: tabData.data.kpis.map((kpi: any) => ({
      pvc_IUKey: isCopyMode ? "I" : constructSeq ? "U" : "I",
      pvc_ConstructSeq: constructSeq?.toString() ?? "",
      pvc_ParameterSeq: kpi.paramId.toString(),
      pvc_Type: kpi.type,
      pvc_ApplicableOn: kpi.applicableOn,
      pvc_ODT: kpi.odt,
      pvc_Basis: kpi.basis,
      pvc_WeightagePercentage: kpi.weightage?.toString() ?? "0",
      pvc_ThresholdPercentage: kpi.performance.threshold.toString(),
      pvc_TargetPercentage: kpi.performance.target.toString(),
      pvc_StretchPercentage: kpi.performance.stretch.toString(),
      pvc_MegaStretchPercentage: kpi.performance.mega?.toString() ?? "0",
      pvc_TotalThresholdPoint: "0",
      pvc_TotalTargetPoint: "0",
      pvc_TotalStretchPoint: "0",
      pvc_TotalMegaStretchPoint: "0",
      pvc_Status: "N",
      pvc_Stream: tabData.constructType,
    })),
  });

  function validateConstruct(obj: any): true | string {
    if (!obj.constructName || obj.constructName.trim() === "") {
      return "Please enter construct name.";
    }
    if (!obj.startDate) {
      return "Please select start date.";
    }
    if (!obj.endDate) {
      return "Please select end date.";
    }
    const startDate = new Date(obj.startDate);
    const endDate = new Date(obj.endDate);
    if (startDate > endDate) {
      return "Start date cannot be greater than end date.";
    }

    const kpis: KpiRow[] = obj?.data?.kpis || [];

    let basisval = kpis.every((item) => item.basis === kpis[0].basis);
    if (!basisval) {
      return "All KPIs must have the same basis.";
    }

    if (obj.constructType === "B2C") {
      for (const kpi of kpis) {
        const {
          parameter,
          performance,
          earning,
          earningType,
          earningCreditDays,
        } = kpi;
        const isCreditDays = earningType === "Credit Days";

        // ── Performance: decimal values, all mandatory (> 0) ──
        if (!performance.threshold || performance.threshold <= 0) {
          return `Performance Threshold for "${parameter}" must be greater than zero.`;
        }
        if (!performance.target || performance.target <= 0) {
          return `Performance Target for "${parameter}" must be greater than zero.`;
        }
        if (!performance.stretch || performance.stretch <= 0) {
          return `Performance Stretch for "${parameter}" must be greater than zero.`;
        }
        if (performance.threshold >= performance.target) {
          return `Performance: Threshold cannot be ≥ Target for "${parameter}".`;
        }
        if (performance.target >= performance.stretch) {
          return `Performance: Target cannot be ≥ Stretch for "${parameter}".`;
        }

        if (isCreditDays) {
          // Credit Days: integer values (whole numbers only)
          const cd = earningCreditDays ?? {
            threshold: 0,
            target: 0,
            stretch: 0,
          };
          if (
            !cd.threshold ||
            cd.threshold <= 0 ||
            !Number.isInteger(cd.threshold)
          ) {
            return `Credit Days Threshold for "${parameter}" must be a positive integer.`;
          }
          if (!cd.target || cd.target <= 0 || !Number.isInteger(cd.target)) {
            return `Credit Days Target for "${parameter}" must be a positive integer.`;
          }
          if (!cd.stretch || cd.stretch <= 0 || !Number.isInteger(cd.stretch)) {
            return `Credit Days Stretch for "${parameter}" must be a positive integer.`;
          }
          if (cd.threshold > cd.target) {
            return `Credit Days: Threshold cannot be > Target for "${parameter}".`;
          }
          if (cd.target > cd.stretch) {
            return `Credit Days: Target cannot be > Stretch for "${parameter}".`;
          }
        } else {
          // Earning %: decimal values, all mandatory (> 0)
          const ern = earning ?? { threshold: 0, target: 0, stretch: 0 };
          if (!ern.threshold || ern.threshold <= 0) {
            return `Earning Threshold for "${parameter}" must be greater than zero.`;
          }
          if (!ern.target || ern.target <= 0) {
            return `Earning Target for "${parameter}" must be greater than zero.`;
          }
          if (!ern.stretch || ern.stretch <= 0) {
            return `Earning Stretch for "${parameter}" must be greater than zero.`;
          }
          if (ern.threshold >= ern.target) {
            return `Earning %: Threshold cannot be ≥ Target for "${parameter}".`;
          }
          if (ern.target >= ern.stretch) {
            return `Earning %: Target cannot be ≥ Stretch for "${parameter}".`;
          }
        }
      }
    } else {
      // HDSSD / INDUSTRIAL: performance decimal, weightage mandatory, mega mandatory
      for (const kpi of kpis) {
        const { parameter, performance, weightage } = kpi;

        if (weightage === undefined || weightage === null || weightage <= 0) {
          return `Weightage for "${parameter}" must be greater than zero.`;
        }
        let sumofweightage = kpis.reduce(
          (sum, item) => sum + (item.weightage ?? 0),
          0,
        );
        if (sumofweightage !== 100) {
          return `Total weightage must be exactly 100%. Current total: ${sumofweightage}%.`;
        }
        if (!performance.threshold || performance.threshold <= 0) {
          return `Performance Threshold for "${parameter}" must be greater than zero.`;
        }
        if (!performance.target || performance.target <= 0) {
          return `Performance Target for "${parameter}" must be greater than zero.`;
        }
        if (!performance.stretch || performance.stretch <= 0) {
          return `Performance Stretch for "${parameter}" must be greater than zero.`;
        }
        if (
          performance.mega === undefined ||
          performance.mega === null ||
          performance.mega <= 0
        ) {
          return `Performance Mega for "${parameter}" must be greater than zero.`;
        }
        if (performance.threshold >= performance.target) {
          return `Performance: Threshold cannot be ≥ Target for "${parameter}".`;
        }
        if (performance.target >= performance.stretch) {
          return `Performance: Target cannot be ≥ Stretch for "${parameter}".`;
        }
        if (performance.stretch >= performance.mega) {
          return `Performance: Stretch cannot be ≥ Mega for "${parameter}".`;
        }
      }
    }

    return true;
  }

  const handleNext = async () => {
    const alldefinationData = extractCurrentTabData(
      activeIndex,
      constructType,
      formData,
      constructSeq,
    );
    console.log("formData", formData);
    console.log("alldefinationData", alldefinationData);

    if (activeIndex === 0) {
      const validationResult = validateConstruct(alldefinationData);
      if (validationResult !== true) {
        toast.warn(validationResult);
        return;
      }
    }

    if (activeIndex < TOTAL_TABS - 1) {
      if (activeIndex === 0) {
        const tabData = extractCurrentTabData(
          activeIndex,
          constructType,
          formData,
          constructSeq,
        );
        console.log("tabData", tabData);
        const latestPly =
          tabData?.constructType === "B2C"
            ? buildPayload(tabData)
            : buildHDSSDPayload(tabData);
        console.log(latestPly);
        try {
          const saveDefData = await postData(
            Controller,
            tabData?.constructType === "B2C"
              ? DbSipEndPoint.SaveDefination
              : DbSipEndPoint.SaveDefination_HDSSDIND,
            latestPly,
          );
          if (saveDefData?.Status === "Warning") {
            toast.warn(`${saveDefData.Message}`);
            return;
          } else {
            const seq =
              !isCopyMode && isEditMode ? constructSeq : saveDefData.Message;
            setConstructSeq(seq);
            if (isCopyMode) setIsCopyMode(false);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("constructid", String(seq));
              return next;
            });
          }
        } catch (err) {
          console.error("Failed to save definition:", err);
        }
      }
      setActiveIndex((i) => i + 1);
    }
  };

  const handleSave = () => {
    toast.success("Construct saved successfully!");
  };

  const handleRefresh = () => {
    setFormData((prev) => {
      const clearPerf = (kpis: KpiRow[]): KpiRow[] =>
        kpis.map((k) => ({
          ...k,
          performance: {
            ...k.performance,
            threshold: 0,
            target: 0,
            stretch: 0,
            ...(k.performance.mega !== undefined ? { mega: 0 } : {}),
          },
        }));
      return {
        ...prev,
        constructName: "",
        startDate: null,
        endDate: null,
        orderMode: [],
        b2c: { ...prev.b2c, kpis: clearPerf(prev.b2c.kpis) },
        hsssd: { ...prev.hsssd, kpis: clearPerf(prev.hsssd.kpis) },
        industrial: {
          ...prev.industrial,
          kpis: clearPerf(prev.industrial.kpis),
        },
      };
    });
    setConstructSeq(null);
    setSearchParams({});
    setIsCopyMode(false);
  };

  const handleCopy = () => {
    setFormData((prev) => ({
      ...prev,
      constructName: "",
      startDate: null,
      endDate: null,
    }));
    setIsCopyMode(true);
  };

  const handlePrevious = () => {
    if (activeIndex > 0) setActiveIndex((i) => i - 1);
  };

  const showNext = activeIndex < TOTAL_TABS - 1;
  const showSave = activeIndex === TOTAL_TABS - 1;
  const prevDisabled = activeIndex === 0;

  return (
    <main>
      <div className="col-12 mb-5">
        <div className="row">
          <div className="col-lg-6 col-md-6 col-sm-12">
            <nav aria-label="breadcrumb" className="drop-in">
              <ol className="breadcrumb mx-0">
                <li className="breadcrumb-item">DB SIP</li>
                <li className="breadcrumb-item">
                  <Link to="/DBSIP/Construct">Construct</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  View
                </li>
              </ol>
            </nav>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="text-end">
              <Link to="/DBSIP/Construct">
                <button type="button" className="btn anime-secondary-btn">
                  <Fa.FaLongArrowAltLeft className="backIcon" /> Back to All
                  Records
                </button>
              </Link>
            </div>
          </div>
        </div>

        <section className="sales-box">
          <div className="row d-flex justify-content-center align-items-center">
            <div className="col-lg-6 col-md-12 col-sm-12">
              <div
                style={{
                  display: "flex",
                  gap: "24px",
                  justifyContent: "start",
                }}
              >
                {(["B2C", "HDSSD", "INDUSTRIAL"] as ConstructType[]).map(
                  (t) => (
                    <label
                      key={t}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                      }}
                    >
                      <input
                        type="radio"
                        name="constructType"
                        checked={constructType === t}
                        disabled={isEditMode}
                        onChange={() => setConstructType(t)}
                        style={{
                          accentColor: "#009343",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                      {t === "INDUSTRIAL" ? "Industrial DB" : t}
                    </label>
                  ),
                )}
              </div>
            </div>
            <div className="col-lg-6 col-md-12 col-sm-12">
              <div className="text-end">
                <button
                  className="btn anime-secondary-btn me-2"
                  onClick={handleRefresh}
                >
                  <Fa.FaSync className="me-2" /> Refresh
                </button>
                {(isEditMode || isCopyMode) && activeIndex === 0 && (
                  <button
                    className="btn anime-secondary-btn me-2"
                    onClick={handleCopy}
                    title="Copy construct — keeps all KPI data, clears name and dates for a new entry"
                  >
                    <Fa.FaCopy className="me-2" /> Copy
                  </button>
                )}
                {showSave && (
                  <button className="btn anime-btn me-2" onClick={handleSave}>
                    Save
                  </button>
                )}
                <button
                  className="btn anime-btn"
                  onClick={handlePrevious}
                  disabled={prevDisabled}
                >
                  <Fa.FaArrowLeft className="me-2" /> Previous
                </button>
                {showNext && (
                  <button className="btn anime-btn ms-2" onClick={handleNext}>
                    Next <Fa.FaArrowRight className="ms-2" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="border bg-white rounded p-3 mt-3">
            {loadingKpis && <Loader />}

            <div className="col-lg-12 col-md-12 col-sm-12 Tab-Pannel">
              {!loadingKpis && (
                <TabView activeIndex={activeIndex} onTabChange={() => {}}>
                  <TabPanel header="Definition">
                    <DefinitionTab
                      formData={formData}
                      setFormData={setFormDataWithSync}
                      constructType={constructType}
                    />
                  </TabPanel>

                  <TabPanel header="Segment Map">
                    <SegmentMapTab
                      formData={formData}
                      setFormData={setFormData}
                      constructType={constructType}
                      constructSeq={constructSeq}
                    />
                  </TabPanel>

                  <TabPanel header="Target Settings">
                    <TargetSettingsTab
                      formData={formData}
                      setFormData={setFormData}
                      constructType={constructType}
                      constructSeq={constructSeq}
                    />
                  </TabPanel>

                  {isB2C && (
                    <TabPanel header="Prod-wise Capping">
                      <ProdCap
                        formData={formData}
                        setFormData={setFormData}
                        constructType={constructType}
                        constructSeq={constructSeq}
                      />
                    </TabPanel>
                  )}
                </TabView>
              )}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
